"use client";
/**
 * useFleet — composes the account's in-flight truth for the composer's presence
 * and island. Second renderer of the SAME truth the home board uses, never a
 * re-implementation:
 * - needs-you rows ride the converging-read interactions feed
 *   (useInteractionsRealtime), one fleet row per task (newest interaction wins);
 * - WORKING is home's exact Working Now rule — lifecycleState processing/queued
 *   (deriveWorkingRows' ACTIVE_STATES) — with the live socket phase as an
 *   overlay for tasks streaming right now. The story-spine phase is NOT a
 *   working signal here: rows killed mid-run stay stuck on "working" forever
 *   (founder 08-04: 30-day-old tasks read LIVE while home showed none);
 * - DONE = lifecycle completed/idle or story-spine done, kept for a day in the
 *   list while only fresh completions glow the light green.
 *
 * What a working row SAYS rides the same signals the task page's status line
 * uses (founder 08-19): the raw lifecycle ('queued' = recorded, no AGENT_START
 * yet), whether this client is driving the task (the live socket phase), and the
 * account's wake flag — read through useWarmupStatus, the task page's own hook,
 * so the two surfaces can never disagree about whether the workspace is waking.
 */
import { useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  useInteractionsRealtime,
  type PendingInteractionRow,
} from "@/hooks/useInteractionsRealtime";
import { useHistoryStore } from "@/stores/historyStore";
import { useTaskStore } from "@/stores/taskStore";
import { useWarmupStatus } from "@/hooks/useWarmupStatus";
import { planNameOf } from "@/lib/engine-plans";
import { deriveFleet, type FleetRow, type FleetSource } from "./fleet";

/** Home's Working Now rule, verbatim (WorkingNowSection.ACTIVE_STATES). */
const ACTIVE_LIFECYCLES = new Set(["processing", "queued"]);
const DONE_LIFECYCLES = new Set(["completed", "idle"]);
const LIVE_PHASES = new Set(["processing", "waiting", "interrupting"]);

export interface FleetPending {
  row: PendingInteractionRow;
  type: string;
}

export function useFleet(enabled: boolean): FleetRow[] {
  const { user } = useAuth();
  const tasks = useTaskStore((s) => s.tasks);
  const taskHistory = useHistoryStore((s) => s.taskHistory);
  const { pending } = useInteractionsRealtime(
    enabled ? (user?.id ?? null) : null
  );
  // Account-level, not per-task: one user, one sandbox, one wake.
  const { warming, placement } = useWarmupStatus(null);

  return useMemo(() => {
    if (!enabled) return [];
    const pendingByTask = new Map<string, PendingInteractionRow>();
    for (const r of pending)
      if (!pendingByTask.has(r.task_id)) pendingByTask.set(r.task_id, r);

    const sources: FleetSource[] = [];
    const seen = new Set<string>();
    for (const row of taskHistory) {
      seen.add(row.id);
      const live = tasks.get(row.id);
      // Working = home's lifecycle rule OR a provably-streaming socket phase.
      const working =
        ACTIVE_LIFECYCLES.has(row.lifecycleState ?? "") ||
        LIVE_PHASES.has(live?.phase ?? "");
      const done =
        !working &&
        (DONE_LIFECYCLES.has(row.lifecycleState ?? "") ||
          row.storyPhase === "done");
      const needsRow = pendingByTask.get(row.id);
      if (!working && !done && !needsRow) continue; // everything else carries no presence
      const lastMs = row.lastActivityAt ? Date.parse(row.lastActivityAt) : NaN;
      sources.push({
        id: row.id,
        title: row.title,
        status: done ? "completed" : "active",
        // Pass the lifecycle through UN-COLLAPSED. Folding 'queued' + a live
        // socket phase into 'processing' (as this once did) threw away the only
        // signal that separates a waking task from a parked one, and made the
        // row claim "Working in the background" before AGENT_START had fired.
        lifecycleState: working
          ? row.lifecycleState === "queued"
            ? "queued"
            : "processing"
          : undefined,
        inFlight: LIVE_PHASES.has(live?.phase ?? ""),
        signingIn:
          live?.sdkStatus === "engine_connecting"
            ? planNameOf(live.runtime)
            : undefined,
        installing: live?.sdkStatus === "engine_installing",
        pulseHeadline: row.pulseHeadline ?? undefined,
        pendingInteraction: needsRow
          ? ({ type: needsRow.kind, row: needsRow } satisfies FleetPending)
          : null,
        updatedAt: Number.isFinite(lastMs) ? lastMs : undefined,
      });
    }
    // A pending ask on a task history hasn't loaded yet still deserves a row.
    for (const [taskId, r] of pendingByTask) {
      if (seen.has(taskId)) continue;
      const createdMs = Date.parse(r.created_at);
      sources.push({
        id: taskId,
        title: r.tasks?.title ?? "Untitled task",
        status: "active",
        pendingInteraction: { type: r.kind, row: r } satisfies FleetPending,
        updatedAt: Number.isFinite(createdMs) ? createdMs : undefined,
      });
    }
    return deriveFleet(sources, Date.now(), { warming, placement });
  }, [enabled, pending, taskHistory, tasks, warming, placement]);
}
